import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ModuleShowcase from '../components/ModuleShowcase';
import { moduleBySlug, moduleHref } from '../data/modules';
import { PILOT, platformStart, appKit } from '../data/plans';
import PageWorld from '../components/site/PageWorld';
import { MODULE } from '../data/worlds';
import { ModuleFilm, moduleFilmsEnabled, MODULE_FILM, PageFilm, pageFilmsEnabled } from '../components/PageFilm';
import {
  FactChips, ScreenLoop, ModesGrid, CapabilityGrid, TrustLadder, WeekWith, CostExamples,
  LimitsTable, NeedsList, CompareRows, MeasuredStrip, Faq, PlanRow,
} from '../components/module';

// /platform/:slug — product page for one name or module. Copy lives in
// data/modules.js so the card, the nav, and this page cannot drift.
// Next follows the public nav, not the array: design, runtime, the agent,
// then the capabilities. Dev Console belongs to App Kit and lives at
// /dev-console (App.js renders this page with slug="dev-console").
//
// Two tiers. Every module has the surface tier (story, scene, outcomes,
// features, steps). A module with a `depth` object also gets the depth
// tier — the loop on screens, the capability grid, the ladder, a week,
// cost at volume, limits, needs, compare, measured, FAQ — rendered in that
// order between the story and the related cards. The FAQ absorbs "what it
// is not" when depth is present.

const PLATFORM_FLOW = ['nqube', 'training-center', 'agentos', 'agent-io', 'nventr-agent', 'knowledge', 'integrations', 'human-review', 'athena', 'listen', 'devops', 'security'];

function nextStep(slug) {
  if (slug === 'dev-console') return { to: '/app-kit#foundation', name: 'The foundation' };
  const i = PLATFORM_FLOW.indexOf(slug);
  if (i === -1 || i === PLATFORM_FLOW.length - 1) return { to: '/#proof', name: 'How it works' };
  const slugNext = PLATFORM_FLOW[i + 1];
  if (slugNext === 'security') return { to: '/security', name: 'Security' };
  const n = moduleBySlug(slugNext);
  if (!n) return { to: '/#proof', name: 'How it works' };
  return { to: moduleHref(n.slug), name: n.name };
}

export default function ModulePage({ slug: fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const mod = moduleBySlug(slug);

  if (!mod) return <Navigate to="/platform" replace />;
  // A module with its own route (Dev Console) is canonical there.
  if (!fixedSlug && mod.route && mod.route !== `/platform/${mod.slug}`) return <Navigate to={mod.route} replace />;

  const next = nextStep(mod.slug);
  const related = (mod.see || [])
    .map((s) => moduleBySlug(s))
    .filter(Boolean);
  const kitPage = mod.slug === 'dev-console';
  const buy = kitPage ? appKit : platformStart;
  const depth = mod.depth || null;

  const film = moduleFilmsEnabled() && MODULE_FILM[mod.slug];
  // A module with its own narrated page film (the Training Center) shows
  // it full width under the hero, like /platform and /pricing do.
  const pageFilm = mod.film && pageFilmsEnabled() ? mod.film : null;
  const heroShot = depth?.hero?.shot;
  const hasScreen = Boolean(heroShot || film || mod.demo);
  return (
    <PageWorld world={MODULE}>
    <div className="cs-store cs-land">
      <header className="cs-store-hero cs-mod-hero cs-mod-hero-stage">
        <div className="cs-mod-hero-copy">
          <p className="cs-mod-kicker">
            {kitPage ? <Link to="/app-kit">App Kit</Link> : <Link to="/platform">Platform</Link>}
            {' / '}
            {kitPage ? mod.name : (mod.kicker || 'module')}
          </p>
          <h1>{depth?.hero?.title || mod.name}</h1>
          <p>{depth?.hero?.line || mod.tagline}</p>
          <FactChips facts={depth?.facts} />
          <div className="cs-land-cta-row" style={depth ? { marginTop: 22 } : undefined}>
            <Link to={buy.href} className="cs-btn cs-btn-primary">
              {buy.cta}
            </Link>
            {kitPage ? (
              <Link to="/app-kit" className="cs-btn cs-btn-outline">
                How the instance works
              </Link>
            ) : (
              <a href={PILOT.href} className="cs-btn cs-btn-outline">
                {PILOT.label}
              </a>
            )}
          </div>
        </div>
        {!pageFilm && hasScreen && (
          <p className="cs-land-micro" style={{ marginTop: 18 }}>
            {heroShot ? (heroShot.cap || 'The console, on synthetic data.') : film ? 'The real console, from our own demo and team organizations.' : (mod.screenNote || 'Product screens recreated with synthetic data.')}
          </p>
        )}
        {!pageFilm && (heroShot
          ? <figure className="mdp-shot mdp-hero-shot"><img src={heroShot.src} alt={heroShot.alt || mod.name} /></figure>
          : film ? <ModuleFilm slug={mod.slug} alt={mod.tagline} /> : <ModuleShowcase slug={mod.slug} />)}
      </header>

      {pageFilm && (
        <PageFilm base={pageFilm.base} prefix={pageFilm.prefix} title={pageFilm.title} summary={pageFilm.summary} chapters={pageFilm.chapters || []} />
      )}

      <section className="cs-store-section" id="what">
        <h2>What it is</h2>
        {(mod.story || [mod.overview]).map((p) => (
          <p className="cs-mod-prose" key={p.slice(0, 48)}>{p}</p>
        ))}
      </section>

      {mod.notThis && !depth && (
        <section className="cs-store-section">
          <h2>What it is not</h2>
          <div className="cs-sw-card cs-mod-callout">
            <p>{mod.notThis}</p>
          </div>
        </section>
      )}

      <section className="cs-store-section" id="who">
        <div className="cs-mod-split">
          <div className="cs-sw-card">
            <h3>Who it is for</h3>
            <p>{mod.audience}</p>
          </div>
          <div className="cs-sw-card">
            <h3>Where you get it</h3>
            <p>{mod.included}</p>
            <PlanRow plans={mod.plans} />
            <Link to="/pricing" className="cs-land-textlink">Pricing →</Link>
          </div>
        </div>
      </section>

      {depth && (
        <>
          <ScreenLoop steps={depth.loop} title={depth.loopTitle} intro={depth.loopIntro} />
          <ModesGrid modes={depth.modes} />
          <CapabilityGrid groups={depth.capabilities} intro={depth.capabilitiesIntro} />
          <TrustLadder rungs={depth.ladder} intro={depth.ladderIntro} note={depth.ladderNote} shot={depth.ladderShot} />
        </>
      )}

      {mod.scene && (
        <section className="cs-store-section" id="scene">
          <h2>{mod.sceneTitle || 'A worked example'}</h2>
          <p className="cs-land-micro" style={{ marginTop: 0 }}>Synthetic example. Not a customer.</p>
          <div className="cs-sw-card cs-mod-scene">
            <p>{mod.scene}</p>
          </div>
        </section>
      )}

      {depth && <WeekWith days={depth.week} intro={depth.weekIntro} />}

      {mod.outcomes && (
        <section className="cs-store-section" id="outcomes">
          <h2>What changes when you have it</h2>
          <div className="cs-land-steps">
            {mod.outcomes.map((o) => (
              <div key={o.name} className="cs-sw-card cs-land-step">
                <h3>{o.name}</h3>
                <p>{o.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {!depth && (
        <section className="cs-store-section">
          <h2>Inside {mod.name}</h2>
          {mod.features && (
            <ul className="cs-land-edge-bullets ms-features">
              {mod.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {mod.walkthrough && (
        <section className="cs-store-section" id="coordinator">
          <h2>{mod.walkthroughTitle || 'Walkthrough'}</h2>
          <div className="cs-land-steps">
            {mod.walkthrough.map((s, i) => (
              <div key={s.name} className="cs-sw-card cs-land-step">
                <div className="cs-land-step-n">{i + 1}</div>
                <h3>{s.name}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {(!depth || !depth.loop) && mod.steps && (
        <section className="cs-store-section" id="how">
          <h2>How it works</h2>
          <div className="cs-land-steps">
            {mod.steps.map((s, i) => (
              <div key={s.name} className="cs-sw-card cs-land-step">
                <div className="cs-land-step-n">{i + 1}</div>
                <h3>{s.name}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
          <div className="cs-land-metered">
            <strong>{mod.name}.</strong> {mod.tiein}
          </div>
        </section>
      )}

      {depth && (
        <>
          <CostExamples rows={depth.cost} intro={depth.costIntro} note={depth.costNote} />
          <LimitsTable rows={depth.limits} intro={depth.limitsIntro} />
          <NeedsList items={depth.needs} intro={depth.needsIntro} />
          <CompareRows rows={depth.compare} intro={depth.compareIntro} />
          <MeasuredStrip items={depth.measured} intro={depth.measuredIntro} />
          {depth.loop && mod.tiein && (
            <section className="cs-store-section">
              <div className="cs-land-metered">
                <strong>{mod.name}.</strong> {mod.tiein}
              </div>
            </section>
          )}
          <Faq items={depth.faq} />
        </>
      )}

      {mod.explainer && (
        <section className="cs-store-section" id={mod.explainer.id}>
          <h2>{mod.explainer.title}</h2>
          {mod.explainer.body.map((p) => <p key={p.slice(0, 48)} className="cs-mod-prose">{p}</p>)}
        </section>
      )}

      {related.length > 0 && (
        <section className="cs-store-section" id="related">
          <h2>The names next to it</h2>
          <div className="cs-mod-related">
            {related.map((r) => (
              <Link key={r.slug} to={moduleHref(r.slug)} className="cs-sw-card cs-mod-related-card">
                <span className="cs-mod-kicker">{r.kicker}</span>
                <h3>{r.name}</h3>
                <p>{r.card}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="cs-land-band">
        <h2>{kitPage ? 'Build it on an instance' : 'See it on your workflow'}</h2>
        <p>
          {kitPage
            ? 'App Kit is the instance — large files, heavy jobs, health checks on every deploy, updates that stay out of your layer. Dev Console is how you change that layer.'
            : 'Platform Start is the self-serve canvas. A first pipeline we stand up with you is delivery, priced apart from the subscription.'}
        </p>
        <div className="cs-land-cta-row cs-land-cta-center">
          <Link to={buy.href} className="cs-btn cs-btn-primary">
            {buy.cta}
          </Link>
          {kitPage ? (
            <Link to="/app-kit" className="cs-btn cs-btn-outline">
              How the instance works
            </Link>
          ) : (
            <a href={PILOT.href} className="cs-btn cs-btn-outline">
              {PILOT.label}
            </a>
          )}
          <Link to={next.to} className="cs-btn cs-btn-outline">
            Next: {next.name} →
          </Link>
        </div>
      </section>
    </div>
    </PageWorld>
  );
}
